import type { ConfigurationOperation } from "@repo/app-types/entities/configurationAgent";
import {
  getOperationCategory,
  getOperationLabel,
  isKnowledgeBaseDocumentOperation,
} from "@repo/app-types/helpers/configurationAgentParser";
import { Loader, LoaderStyle } from "@repo/ui/Loader/loader";
import {
  IconCheck,
  IconCircleMinus,
  IconCirclePlus,
  IconFileText,
  IconPencil,
} from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";
import { useConfigurationAssistantContext } from "./configurationAssistantContext.js";

export interface InlineChangeCardProps {
  /** Stable ID for tracking applied state across re-renders */
  operationId: string;
  operation: ConfigurationOperation;
}

/**
 * A change the copilot wrote inline in its message, with the control that
 * applies it. Covers knowledge-base documents; learning-journey changes have
 * their own card, and playbook and voice agent changes are proposal rows now
 * rather than anything inline.
 */
export const InlineChangeCard: FC<InlineChangeCardProps> = ({
  operationId,
  operation,
}) => {
  const { applyOperation, isOperationApplied, getDocumentDisplayName } =
    useConfigurationAssistantContext();
  const [isLoading, setIsLoading] = useState(false);

  const category = getOperationCategory(operation);
  const label = getOperationLabel(operation);
  const isApplied = isOperationApplied(operationId);
  const isKbDocument = isKnowledgeBaseDocumentOperation(operation);

  const handleApply = async () => {
    setIsLoading(true);
    try {
      await applyOperation(operationId, operation);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={twMerge(
        "overflow-hidden rounded-xl border border-border-light bg-white shadow-xs",
        category === "add" && "border-emerald-200",
        category === "update" && "border-blue-200",
        category === "remove" && "border-rose-200",
      )}
    >
      {/* Header */}
      <div
        className={twMerge(
          "flex items-center gap-2 px-3 py-2",
          category === "add" && "bg-emerald-50",
          category === "update" && "bg-blue-50",
          category === "remove" && "bg-rose-50",
        )}
      >
        <OperationIcon
          category={category}
          isKnowledgeBaseDocument={isKbDocument}
        />
        <span
          className={twMerge(
            "text-xs font-semibold",
            category === "add" && "text-emerald-700",
            category === "update" && "text-blue-700",
            category === "remove" && "text-rose-700",
          )}
        >
          {label}
        </span>
      </div>

      {/* Content */}
      <div className="px-3 py-2.5">
        <OperationDetails
          operation={operation}
          category={category}
          getDocumentDisplayName={getDocumentDisplayName}
        />
      </div>

      {/* Apply button */}
      <div className="border-t border-gray-100 px-3 py-2">
        <button
          type="button"
          onClick={handleApply}
          disabled={isApplied || isLoading}
          className={twMerge(
            "flex w-full items-center justify-center gap-1.5 rounded-lg py-1.5 text-xs font-medium transition-colors",
            isApplied || isLoading
              ? "cursor-default bg-gray-100 text-muted-foreground"
              : twMerge(
                  category === "add" &&
                    "bg-emerald-100 text-emerald-700 hover:bg-emerald-200",
                  category === "update" &&
                    "bg-blue-100 text-blue-700 hover:bg-blue-200",
                  category === "remove" &&
                    "bg-rose-100 text-rose-700 hover:bg-rose-200",
                ),
          )}
        >
          {isLoading ? (
            <>
              <Loader
                style={LoaderStyle.SPINNER}
                className="size-3.5 fill-gray-500"
              />
              {getLoadingButtonLabel(operation.type)}
            </>
          ) : isApplied ? (
            <>
              <IconCheck className="size-3.5" />
              {getAppliedButtonLabel(operation.type)}
            </>
          ) : (
            getButtonLabel(operation.type)
          )}
        </button>
      </div>
    </div>
  );
};

const OperationIcon: FC<{
  category: "add" | "update" | "remove";
  isKnowledgeBaseDocument?: boolean;
}> = ({ category, isKnowledgeBaseDocument }) => {
  const iconClass = twMerge(
    "size-3.5",
    category === "add" && "text-emerald-600",
    category === "update" && "text-blue-600",
    category === "remove" && "text-rose-600",
  );

  if (isKnowledgeBaseDocument) {
    return <IconFileText className={iconClass} />;
  }

  switch (category) {
    case "add":
      return <IconCirclePlus className={iconClass} />;
    case "update":
      return <IconPencil className={iconClass} />;
    case "remove":
      return <IconCircleMinus className={iconClass} />;
  }
};

const OperationDetails: FC<{
  operation: ConfigurationOperation;
  category: "add" | "update" | "remove";
  getDocumentDisplayName: (slugifiedName: string) => string | undefined;
}> = ({ operation, category, getDocumentDisplayName }) => {
  const fields = getOperationFields(operation, getDocumentDisplayName);

  return (
    <div className="space-y-2.5">
      {fields.map((field, idx) => (
        <div key={idx}>
          <span className="text-[10px] font-medium text-muted-foreground">
            {field.label}
          </span>
          {field.type === "badge" ? (
            <div className="mt-0.5">
              <Badge category={category}>{field.value}</Badge>
            </div>
          ) : (
            <p
              className={twMerge(
                "mt-0.5 text-[13px] leading-relaxed whitespace-pre-wrap text-foreground",
                category === "remove" && "line-through",
              )}
            >
              {field.value}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

interface FieldItem {
  label: string;
  value: string;
  type: "text" | "badge";
}

type DocumentDisplayNameLookup = (slugifiedName: string) => string | undefined;

/**
 * Get all fields for an operation with their labels
 */
function getOperationFields(
  operation: ConfigurationOperation,
  getDocumentDisplayName: DocumentDisplayNameLookup,
): FieldItem[] {
  const fields: FieldItem[] = [];

  switch (operation.type) {
    case "update-knowledge-base-document":
      fields.push({
        label: "Article",
        value:
          getDocumentDisplayName(operation.documentName) ??
          operation.documentName,
        type: "badge",
      });
      fields.push({
        label: "Current text",
        value: operation.patch.find,
        type: "text",
      });
      fields.push({
        label: "New text",
        value: operation.patch.replace,
        type: "text",
      });
      break;
  }

  return fields;
}

function getButtonLabel(operationType: ConfigurationOperation["type"]): string {
  switch (operationType) {
    case "update-knowledge-base-document":
      return "Apply changes";
    default:
      return "Apply change";
  }
}

function getLoadingButtonLabel(
  operationType: ConfigurationOperation["type"],
): string {
  switch (operationType) {
    case "update-knowledge-base-document":
      return "Applying...";
    default:
      return "Applying...";
  }
}

function getAppliedButtonLabel(
  operationType: ConfigurationOperation["type"],
): string {
  switch (operationType) {
    case "update-knowledge-base-document":
      return "Changes applied";
    default:
      return "Change applied";
  }
}

const Badge: FC<{
  category: "add" | "update" | "remove";
  children: string;
}> = ({ category, children }) => (
  <span
    className={twMerge(
      "inline-flex rounded-md px-1.5 py-0.5 text-[11px] font-medium",
      category === "add" && "bg-emerald-100 text-emerald-700",
      category === "update" && "bg-blue-100 text-blue-700",
      category === "remove" && "bg-rose-100 text-rose-700",
    )}
  >
    {children}
  </span>
);
